import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { authAPI } from "../authAPI";
import { jwtDecode } from "jwt-decode";
import AuthLayout, {
  AuthAlert,
  AuthFieldError,
  AuthOtpModal,
  AuthSecurityNote,
  EyeIcon,
  authButtonClass,
  authEyeButtonClass,
  authInputClass,
  authInputErrorClass,
  authLabelClass,
  authLinkClass,
} from "./AuthLayout";
import ProductAuthLayout, { PRODUCTS } from "./ProductAuthLayout";

// Industry, address and city are no longer collected at signup; the user can fill them in from Account Settings
const PLACEHOLDER_PROFILE_VALUE = "None";

// `product` (e.g. "igr-creatives") switches to that product's branded screen; signup logic is shared
const Signup = ({ onSignup, product }) => {
  const Layout = product ? ProductAuthLayout : AuthLayout;
  const loginPath = product ? `/${product}/login` : "/login";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [signupData, setSignupData] = useState(null);

  // Validation State
  const [touched, setTouched] = useState({});

  // Validation Logic
  const validate = () => {
    const errors = {};
    if (!name.trim()) errors.name = "Full name is required";
    if (!phone.trim()) errors.phone = "Phone number is required";
    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = "Please enter a valid email address";
    }
    if (!country.trim()) errors.country = "Country is required";
    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    if (!acceptTerms) errors.terms = "You must accept the terms";
    return errors;
  };

  const errors = validate();

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Auto-fetch location on mount
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();
        if (data.country_name) setCountry(data.country_name);
      } catch (error) {
        console.error("Failed to fetch location:", error);
      }
    };
    fetchLocation();
  }, []);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (
      email.trim() &&
      name.trim() &&
      phone.trim() &&
      country.trim() &&
      password.trim() &&
      acceptTerms
    ) {
      setIsLoading(true);
      setError("");

      const userData = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        country: country.trim(),
        city: PLACEHOLDER_PROFILE_VALUE,
        address1: PLACEHOLDER_PROFILE_VALUE,
        password: password.trim(),
        industry: PLACEHOLDER_PROFILE_VALUE,
      };

      // Step 1: Send verification OTP FIRST (before creating account)
      const otpResult = await authAPI.sendVerificationOTP(userData);

      if (!otpResult.success) {
        setError(otpResult.error);
        setIsLoading(false);
        return;
      }

      // Store signup data and show OTP modal
      setSignupData(userData);
      setShowOtpModal(true);
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.trim().length === 6) {
      setOtpLoading(true);
      setError("");

      // Step 2: Verify OTP
      const verifyResult = await authAPI.verifyEmailOTP(
        signupData.email,
        otp.trim()
      );

      if (!verifyResult.success) {
        setError(verifyResult.error);
        setOtpLoading(false);
        return;
      }

      // Step 3: Create user account AFTER email is verified
      const signupResult = await authAPI.signupUser(signupData);

      if (!signupResult.success) {
        setError(signupResult.error);
        setOtpLoading(false);
        return;
      }

      // Step 4: Auto login after successful account creation
      const loginResult = await authAPI.loginWithPassword(
        signupData.email,
        signupData.password
      );

      if (loginResult.success) {
        const userData = {
          id: loginResult.data.customer?.companyCustomerId || loginResult.data.customerId || Date.now(),
          name: signupData.name,
          email: signupData.email,
          phone: signupData.phone,
          avatar: signupData.name.charAt(0).toUpperCase(),
          isPro: false,
          isNewSignup: true,
          ...loginResult.data,
          ...(loginResult.data.customer || {}), // Flatten customer data to top level
          token: loginResult.data.token, // Ensure top-level token is preserved
          userId: (loginResult.data.token ? jwtDecode(loginResult.data.token).userId : null) // Explicitly expose userId
        };

        onSignup(userData);
      } else {
        // Account created but login failed
        setError("Account created successfully! Redirecting to login...");
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      }

      setOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError("");
    setOtpLoading(true);

    const otpResult = await authAPI.sendVerificationOTP(signupData);

    if (!otpResult.success) {
      setError(otpResult.error);
    } else {
      setError("OTP resent successfully!");
    }

    setOtpLoading(false);
  };

  const fieldClass = (field) =>
    `${authInputClass} ${touched[field] && errors[field] ? authInputErrorClass : ""}`;

  const canSubmit =
    email.trim() &&
    password.trim() &&
    confirmPassword.trim() &&
    password === confirmPassword &&
    name.trim() &&
    phone.trim() &&
    country.trim() &&
    acceptTerms &&
    !isLoading;

  return (
    <Layout
      product={product}
      title={product ? "Create Your Account" : "Start your free trial"}
      subtitle={product ? PRODUCTS[product].signupIntro : "No credit card required."}
      headerLink={{ to: loginPath, label: "Log In" }}
      cardFooter={
        <>
          Already have an account?{" "}
          <Link to={loginPath} className={authLinkClass}>
            Log In
          </Link>
        </>
      }
    >
      <AuthAlert message={showOtpModal ? "" : error} />

      {/* Signup Form */}
      <form onSubmit={handleSendOTP} className="grid gap-4">
        <div>
          <label htmlFor="signup-name" className={authLabelClass}>
            Full name
          </label>
          <input
            id="signup-name"
            type="text"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => handleBlur("name")}
            placeholder="Your name"
            autoComplete="name"
            className={fieldClass("name")}
            required
            disabled={showOtpModal}
          />
          {touched.name && errors.name && <AuthFieldError>{errors.name}</AuthFieldError>}
        </div>

        <div>
          <label htmlFor="signup-email" className={authLabelClass}>
            Work email
          </label>
          <input
            id="signup-email"
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => handleBlur("email")}
            placeholder="you@company.com"
            autoComplete="email"
            className={fieldClass("email")}
            required
            disabled={showOtpModal}
          />
          {touched.email && errors.email && <AuthFieldError>{errors.email}</AuthFieldError>}
        </div>

        <div>
          <label htmlFor="signup-phone" className={authLabelClass}>
            Phone number
          </label>
          <input
            id="signup-phone"
            type="tel"
            name="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onBlur={() => handleBlur("phone")}
            placeholder="+91 98765 43210"
            autoComplete="tel"
            className={fieldClass("phone")}
            required
            disabled={showOtpModal}
          />
          {touched.phone && errors.phone && <AuthFieldError>{errors.phone}</AuthFieldError>}
        </div>

        <div>
          <label htmlFor="signup-country" className={authLabelClass}>
            Country
          </label>
          <input
            id="signup-country"
            type="text"
            name="country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            onBlur={() => handleBlur("country")}
            placeholder="Your country"
            autoComplete="country-name"
            className={fieldClass("country")}
            required
            disabled={showOtpModal}
          />
          {touched.country && errors.country && <AuthFieldError>{errors.country}</AuthFieldError>}
        </div>

        {/* Password & Confirm Password side by side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="signup-password" className={authLabelClass}>
              Password
            </label>
            <div className="relative">
              <input
                id="signup-password"
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => handleBlur("password")}
                placeholder="Create password"
                autoComplete="new-password"
                className={`${fieldClass("password")} pr-12`}
                required
                minLength={6}
                disabled={showOtpModal}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={authEyeButtonClass}
                disabled={showOtpModal}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>
            {touched.password && errors.password && <AuthFieldError>{errors.password}</AuthFieldError>}
          </div>

          <div>
            <label htmlFor="signup-confirm-password" className={authLabelClass}>
              Confirm password
            </label>
            <div className="relative">
              <input
                id="signup-confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => handleBlur("confirmPassword")}
                placeholder="Re-enter password"
                autoComplete="new-password"
                className={`${fieldClass("confirmPassword")} pr-12`}
                required
                minLength={6}
                disabled={showOtpModal}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className={authEyeButtonClass}
                disabled={showOtpModal}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                <EyeIcon open={showConfirmPassword} />
              </button>
            </div>
            {touched.confirmPassword && errors.confirmPassword && (
              <AuthFieldError>{errors.confirmPassword}</AuthFieldError>
            )}
          </div>
        </div>

        {/* Terms */}
        <label htmlFor="terms" className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            id="terms"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-0.5 w-4 h-4 shrink-0 rounded accent-cyan-500"
            required
            disabled={showOtpModal}
          />
          <span className="text-[13px] leading-normal text-gray-600 dark:text-gray-400">
            I agree to the{" "}
            <a
              href="https://www.infiniumglobalresearch.com/terms-condition"
              target="_blank"
              rel="noopener noreferrer"
              className={authLinkClass}
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              href="https://www.infiniumglobalresearch.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className={authLinkClass}
            >
              Privacy Policy
            </a>
          </span>
        </label>

        {!showOtpModal && (
          <button type="submit" disabled={!canSubmit} className={authButtonClass}>
            {isLoading ? "Sending OTP..." : product ? "Create Account" : "Start Free Trial"}
          </button>
        )}
      </form>

      <AuthSecurityNote>
        Secure OTP email verification. We never share your email.
      </AuthSecurityNote>

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <AuthOtpModal
          title="Verify your email"
          email={signupData?.email}
          error={error}
          otp={otp}
          setOtp={setOtp}
          onSubmit={handleVerifyOTP}
          loading={otpLoading}
          submitLabel="Verify & Create Account"
          loadingLabel="Creating account..."
          onResend={handleResendOTP}
          onCancel={() => {
            setShowOtpModal(false);
            setOtp("");
            setError("");
          }}
        />
      )}
    </Layout>
  );
};

export default Signup;
